import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { Building2, MapPin } from "lucide-react";
import FieldConfigurationAPI from "../../../../lib/api/FieldConfiguration";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../components/SearchBar";
import PaginationControls from "../components/PaginationControls";
import { getActiveCompanyOptions } from "./AccessGroupsList";
import { SELECTED_COMPANY_KEY } from "../../../../lib/local/SelectedCompany";
import "./RolesList.css";
import "./JobLocationsList.css";

export const SELECTED_COMPANY_STORAGE_KEY = SELECTED_COMPANY_KEY;

export function formatAddress(job) {
  if (job.location_address) return job.location_address;
  const parts = [job.address, job.city, job.state, job.zipcode].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : null;
}

export function hasLocationSet(job) {
  return job.latitude != null && job.longitude != null;
}

export default function JobLocationsList({ onJobSelect }) {
  const { companies } = useContext(FieldConfigurationContext);

  const [jobs, setJobs] = useState({ data: [], loading: false, error: null });
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showUnsetOnly, setShowUnsetOnly] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 20,
  });

  const companyOptions = useMemo(
    () => getActiveCompanyOptions(companies),
    [companies.data, companies.codesMap, companies.enabledData]
  );

  useEffect(() => {
    if (companyOptions.length === 0) return;
    if (
      selectedCompanyUuid &&
      companyOptions.some((opt) => opt.value === selectedCompanyUuid)
    ) {
      return;
    }
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    const initial =
      stored && companyOptions.some((opt) => opt.value === stored)
        ? stored
        : companyOptions[0].value;
    setSelectedCompanyUuid(initial);
  }, [companyOptions, selectedCompanyUuid]);

  const fetchJobs = useCallback(async () => {
    if (!selectedCompanyUuid) return;
    setJobs((prev) => ({ ...prev, loading: true, error: null }));
    const response = await FieldConfigurationAPI.getJobs(
      null,
      selectedCompanyUuid
    );
    if (response.status === 200 && response.data) {
      setJobs({ data: response.data, loading: false, error: null });
    } else {
      setJobs({ data: [], loading: false, error: "Failed to load jobs." });
    }
  }, [selectedCompanyUuid]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleCompanyChange = (e) => {
    const uuid = e.target.value;
    setSelectedCompanyUuid(uuid);
    localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, uuid);
  };

  const filteredJobs = useMemo(() => {
    let filtered = jobs.data;

    if (showUnsetOnly) {
      filtered = filtered.filter((job) => !hasLocationSet(job));
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.code?.toLowerCase().includes(lower) ||
          job.description?.toLowerCase().includes(lower)
      );
    }

    return filtered;
  }, [jobs.data, showUnsetOnly, searchTerm]);

  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [filteredJobs.length]);

  const paginatedJobs = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    return filteredJobs.slice(
      startIndex,
      startIndex + paginationConfig.pageSize
    );
  }, [filteredJobs, paginationConfig]);

  return (
    <div className="roles-list job-locations-list">
      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">Job Locations</h2>
            {companyOptions.length > 0 && (
              <div className="roles-list__company-filter">
                <Building2
                  size={18}
                  style={{ color: "rgba(100, 116, 139, 1)", flexShrink: 0 }}
                />
                <select
                  className="roles-list__company-select"
                  value={selectedCompanyUuid}
                  onChange={handleCompanyChange}
                  title={
                    companyOptions.find(
                      (opt) => opt.value === selectedCompanyUuid
                    )?.label || ""
                  }
                >
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search jobs..."
          />
          <label className="job-locations-list__filter-checkbox">
            <input
              type="checkbox"
              checked={showUnsetOnly}
              onChange={(e) => setShowUnsetOnly(e.target.checked)}
            />
            <span>Show only jobs without a location set</span>
          </label>
        </div>
      </div>

      {jobs.loading && (
        <div className="roles-list__loading">
          <p>Loading jobs...</p>
        </div>
      )}

      {jobs.error && (
        <div className="roles-list__error">
          <p>{jobs.error}</p>
        </div>
      )}

      {!jobs.loading && !jobs.error && (
        <>
          <div className="job-locations-list__rows">
            {paginatedJobs.length === 0 && (
              <div className="job-locations-list__empty">
                No jobs found for this company.
              </div>
            )}
            {paginatedJobs.map((job) => {
              const locationSet = hasLocationSet(job);
              const address = formatAddress(job);
              return (
                <div
                  key={job.uuid}
                  className="job-locations-list__row"
                  onClick={() => onJobSelect && onJobSelect(job)}
                >
                  <MapPin
                    size={20}
                    className={
                      locationSet
                        ? "job-locations-list__pin job-locations-list__pin--set"
                        : "job-locations-list__pin"
                    }
                  />
                  <div className="job-locations-list__info">
                    <div className="job-locations-list__name">
                      {job.description} ({job.code})
                    </div>
                    <div className="job-locations-list__address">
                      {address || "Address Unavailable"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={filteredJobs.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}
    </div>
  );
}
